import { Loader } from '@storybook';
import { useCallback, useEffect, useRef, useState } from 'react';
import { API_URL } from 'constant';
import { REACT_APP_KYC_FRONTEND_DOMAIN, REACT_APP_KYC_KYB_ID } from 'envs';
import { useNetwork, useNotification } from 'hooks';
import './kyc-web-components.scss';

declare global {
	// eslint-disable-next-line @typescript-eslint/no-namespace
	namespace JSX {
		interface IntrinsicElements {
			'simplici-onboarding': any;
		}
	}
}

interface IFrameData {
	type: 'handleChange' | 'onComplete';
	data: Record<string, any>;
}

interface IKycWebComponents {
	type: 'kyc' | 'kyb' | string;
	handleSuccess: () => void;
	businessType?: string;
}
const { BUSINESS_COMPLIANCE } = API_URL;
export const KycWebComponents = ({
	type,
	handleSuccess,
	businessType,
}: IKycWebComponents) => {
	const [isLoading, setIsLoading] = useState(true);
	const [sessionCode, setSessionCode] = useState('');
	const [iFrameLoading, setIFrameLoading] = useState(true);
	const completed = useRef(false);

	// Hooks
	const { post } = useNetwork();
	const { errorNotification } = useNotification();

	const handleSessionCode = useCallback(async () => {
		await post(BUSINESS_COMPLIANCE, {
			qrId: REACT_APP_KYC_KYB_ID,
			type: businessType,
		}).then(res => {
			if (res?.sessionCode?.length > 0) {
				setSessionCode(res.sessionCode);
			} else {
				errorNotification('Session Not Found!');
			}
			setIsLoading(false);
		});
		return null;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const appendScripts = async () => {
		// await InjectScript([
		// 	{
		// 		type: 'script',
		// 		// link: ${REACT_APP_KYC_FRONTEND_BUILD},
		// 		link: REACT_APP_KYC_FRONTEND_DOMAIN,
		// 	},
		// ]);
		await handleSessionCode();
		setIsLoading(false);
	};

	const onStepChange = useCallback(
		async function (data: any) {
			// __simplici_single_completion__: the identity leg finishes once. Without
			// this latch a duplicated signal calls handleSuccess again, and for the
			// entity flow handleSuccess is onSubmitBusinessIdentity — which registers
			// the business and then navigates. Two of those in flight at once race:
			// one can take the payment branch while the other falls through to the
			// UNDER_REVIEW branch and routes away to the waiting page, unmounting the
			// modal. Whichever resolves last wins, so the journey ended on the
			// dashboard at random.
			if (completed.current) return;
			if (type === 'kyc' && data.key === 'kybVerification') {
				completed.current = true;
				handleSuccess();
				return;
			}
			if (data.key === 'successScreenCompletion') {
				completed.current = true;
				handleSuccess();
			}
		},
		[handleSuccess, type]
	);

	// eslint-disable-next-line @typescript-eslint/no-unused-vars, @typescript-eslint/no-empty-function
	(window as any).onSessionComplete = function (_data: any) { };

	// Session bootstrap runs once per mount; it must not be re-run when the
	// message handler identity changes.
	useEffect(() => {
		appendScripts();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// __simplici_message_listener_cleanup__: this listener used to be added inside
	// the mount effect with an inline handler and NO cleanup, so every mount of
	// this component left another live listener on window. One postMessage from
	// the iframe then invoked onStepChange — and therefore handleSuccess — once per
	// leaked listener. Keeping a reference and removing it on unmount makes the
	// completion signal fire exactly one navigation.
	useEffect(() => {
		const onMessage = (event: MessageEvent) => {
			// For security, validate event.origin if the parent window accepts
			// messages from multiple sources.
			const receivedData: IFrameData = event.data;
			if (receivedData?.type === 'handleChange') {
				onStepChange(receivedData.data);
			}
		};
		window.addEventListener('message', onMessage);
		return () => window.removeEventListener('message', onMessage);
	}, [onStepChange]);

	return (
		<div className="Kyc--WebComponents__container">
			{(isLoading || iFrameLoading) && (
				<div className="BusinessSession--loader BusinessSession--iframe-loader">
					<Loader />
					<div className="Business-Rejected--title">
						Please wait while we setup signup flow for you.
					</div>
				</div>
			)}
			{!isLoading && (
				<iframe
					src={/* __simplici_documentless_billing__ */ (process.env.REACT_APP_KYC_DOCUMENTLESS === 'true' && type !== 'kyb' ? `${REACT_APP_KYC_FRONTEND_DOMAIN}/tier1/${sessionCode}` : `${REACT_APP_KYC_FRONTEND_DOMAIN}/${sessionCode}?type=complex&isfromsignup=true`)}
					// src="http://localhost:3001/da27138f-a177-4b42-8c99-abc8300800cc?type=complex"
					allow="camera; microphone; geolocation;"
					style={{ border: 'none', height: 'calc(100vh - 60px)', width: '100%' }}
					onLoad={() => setIFrameLoading(false)}
				/>
			)}
		</div>
	);
};
